import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { track } from "../lib/analytics";
import { usePlayer } from "../context/PlayerContext";
import { useSettings } from "../context/SettingsContext";
import { artistSchema, breadcrumbSchema } from "../lib/schema";
import Seo from "../components/Seo";
import FollowStrip from "../components/FollowStrip";
import RelatedReleases from "../components/RelatedReleases";
import EmailCapture from "../components/EmailCapture";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";

const PILLARS = [
  { k: "Dominican Roots", v: "Dembow, bachata and merengue at home — the rhythmic DNA under every record." },
  { k: "Caribbean Influence", v: "Island percussion, call-and-response hooks and heat built for open-air nights." },
  { k: "Reggaetón", v: "Rolling dembow drums and perreo energy — the language of the modern Latin club." },
  { k: "Hip-Hop", v: "Trap-influenced low end, street storytelling and the confidence of the mixtape era." },
  { k: "Club-Oriented Production", v: "Every song is arranged like a DJ set: tension, drop, release. Dance floor first." },
  { k: "Independent", v: "Written, produced and released independently — a direct line between artist and fans." },
];

function Metric({ label, value }) {
  if (!value) return null;
  return (
    <div className="rounded-2xl border border-white/10 bg-surface/60 px-5 py-4">
      <p className="font-display text-3xl font-bold text-gold">{value}</p>
      <p className="mt-1 text-[11px] font-semibold uppercase tracking-widest text-white/50">{label}</p>
    </div>
  );
}

export default function About() {
  const settings = useSettings();
  const { playRelease } = usePlayer();
  const [top, setTop] = useState([]);
  useEffect(() => {
    window.scrollTo(0, 0);
    track("about_view", {});
    api.get("/releases").then((r) => setTop((r.data || []).slice(0, 3))).catch(() => {});
  }, []);
  const m = settings?.metrics || {};
  const paragraphs = (settings?.bioFull || "").split(/\n\s*\n/).filter(Boolean);

  return (
    <div className="grain relative min-h-screen pb-32">
      <Seo
        title="About — Dominican Latin Urban Artist, Reggaetón & Hip-Hop Producer"
        description={settings?.bioShort}
        path="/about"
        type="profile"
        jsonLd={[artistSchema(settings), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About", path: "/about" }], settings)]}
      />
      <div className="px-6 pt-28 sm:px-10 lg:px-16">
        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-16">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-gold">About</p>
            <h1 className="mt-3 font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight text-white sm:text-6xl">DJ Código</h1>
            <p className="mt-3 font-display text-xl font-semibold text-white/90 sm:text-2xl">{settings?.tagline || "Dominican Roots. Global Sound."}</p>
            <p className="mt-1 text-sm text-white/50">{settings?.genreLine}</p>
            <p data-testid="about-short-bio" className="mt-8 text-lg leading-relaxed text-white/80">{settings?.bioShort}</p>
            <div data-testid="about-full-bio" className="mt-6 space-y-4 text-base leading-relaxed text-white/65">
              {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            <FollowStrip settings={settings} placement="about" className="mt-10" />
          </div>
          <div className="space-y-6">
            <Reveal>
              <div className="relative overflow-hidden rounded-3xl border border-white/10">
                <img src={settings?.artistImage} alt="DJ Código — Dominican reggaetón and Latin hip-hop artist" className="aspect-[4/5] w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent" />
              </div>
            </Reveal>
            {(m.spotifyMonthlyListeners || m.youtubeSubscribers || m.totalStreams) && (
              <div data-testid="about-metrics" className="grid grid-cols-3 gap-3">
                <Metric label="Spotify monthly" value={m.spotifyMonthlyListeners} />
                <Metric label="YouTube subs" value={m.youtubeSubscribers} />
                <Metric label="Streams" value={m.totalStreams} />
              </div>
            )}
            {top.length > 0 && (
              <div data-testid="about-top-songs" className="rounded-3xl border border-white/10 bg-surface/60 p-5">
                <p className="mb-3 text-[11px] font-bold uppercase tracking-widest text-gold">Start here — top songs</p>
                {top.map((r) => (
                  <div key={r.internalId} className="flex items-center gap-3 border-b border-white/5 py-2.5 last:border-0">
                    <button aria-label={`Play ${r.title}`} data-testid={`about-play-${r.slug}`} onClick={() => playRelease(r)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold text-black"><Play size={14} fill="currentColor" className="ml-0.5" /></button>
                    <Link to={`/music/${r.slug}`} className="min-w-0 flex-1 truncate text-sm font-semibold text-white hover:text-gold">{r.title}</Link>
                    <span className="text-[10px] uppercase tracking-widest text-white/40">{r.releaseType}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mx-auto mt-20 max-w-6xl">
          <Reveal><p className="mb-2 text-xs font-bold uppercase tracking-[0.3em] text-gold">The sound</p><h2 className="mb-8 font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-5xl">What makes the Código</h2></Reveal>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PILLARS.map((p, i) => (
              <Reveal key={p.k} delay={i * 0.05}>
                <div className="h-full rounded-2xl border border-white/10 bg-surface/60 p-6">
                  <p className="font-display text-lg font-semibold text-white">{p.k}</p>
                  <p className="mt-2 text-sm leading-relaxed text-white/60">{p.v}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/#latest-releases" data-testid="about-explore" className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-4 text-sm font-bold uppercase tracking-wide text-black">Explore the catalog <ArrowRight size={16} /></Link>
            <Link to="/videos" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3.5 text-xs font-semibold uppercase tracking-wide text-white/80 hover:border-gold/50 hover:text-gold">Watch videos</Link>
            <Link to="/press" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3.5 text-xs font-semibold uppercase tracking-wide text-white/80 hover:border-gold/50 hover:text-gold">Press / EPK</Link>
          </div>
        </div>
      </div>
      <RelatedReleases current={null} overline="Discography" title="Listen to DJ Código" limit={8} />
      <EmailCapture source="about" />
      <Footer settings={settings} />
    </div>
  );
}
